<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin2 === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div
      class="navbar-header expanded"
      :class="[`brand-${brand}`]"
    >
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <template v-if="brand === 'se'">
              <b-link
                class="navbar-brand"
                to="/"
              >
                <span class="brand-logo">
                  <b-img
                    :src="seBrand.companyLogo1"
                    alt="logo 1"
                  />
                  <b-img
                    v-if="seBrand.companyLogo2 !==''"
                    :src="seBrand.companyLogo2"
                    alt="logo 2"
                    class="ml-25"
                  />
                </span>
              </b-link>
            </template>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  BLink,
  BImg,
} from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import BrandLogosMixin from '@/mixins/BrandLogosMixin.vue'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  mixins: [BrandLogosMixin],
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  computed: {
    navMenuItems() {
      const showUpgrade = this.$store.getters['auth/showPremiumUpgrade']
      return navMenuItems.map(item => (item.children ? {
        ...item,
        children: item.children.filter(child => child.route !== 'learn-more-premium' || showUpgrade),
      } : item))
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin2 } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const {
      brand, appName, appLogoImage, companyLogoImage1, companyLogoImage2,
    } = $themeConfig.app

    return {
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin2,

      // Brand details
      brand,
      appName,
      appLogoImage,
      companyLogoImage1,
      companyLogoImage2,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.navbar-header {
  flex-shrink: 0;
}

.shadow-bottom {
  flex-shrink: 0;
}

.main-menu-content {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  min-height: 0 !important;
}

.main-menu-footer {
  flex-shrink: 0 !important;
  background: inherit;
  position: relative;
  z-index: 10;
  min-height: 60px !important;
  width: 100%;
  list-style: none;
  padding: 1rem 1.75rem 1.5rem 1.75rem !important;

  ::v-deep .dropdown-user {
    list-style: none;

    .btn-link {
      text-decoration: none;
      padding-left: 0;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  ::v-deep .user-nav {
    align-items: flex-start;
    line-height: 1.3;

    .user-name {
      font-size: 1.1rem;
    }

    span {
      font-size: 1.1rem;
    }
  }

  ::v-deep .dropdown-menu {
    bottom: 100% !important;
    top: auto !important;
    margin-bottom: 0.5rem;
  }

  ::v-deep .nav-item {
    list-style: none;

    .nav-link {
      font-size: 1.25rem;
    }

    svg {
      width: 24px;
      height: 24px;
    }
  }
}
</style>

<style lang="scss">
body:not(.dark-layout) {
  .main-menu-footer {
    .dropdown-user {
      .btn-link,
      .btn-link:hover,
      .btn-link:focus,
      .btn-link:active,
      button {
        color: #6e6b7b !important;
      }
    }

    .user-nav,
    .user-nav *,
    .user-name,
    span {
      color: #6e6b7b !important;
    }

    .nav-item .nav-link,
    .nav-item svg {
      color: #6e6b7b !important;
    }
  }
}

body.dark-layout {
  .main-menu-footer {
    .dropdown-user {
      .btn-link,
      .btn-link:hover,
      .btn-link:focus,
      .btn-link:active,
      button {
        color: #b4b7bd !important;
      }
    }

    .user-nav,
    .user-nav *,
    .user-name,
    span {
      color: #b4b7bd !important;
    }

    .nav-item .nav-link,
    .nav-item svg {
      color: #b4b7bd !important;
    }
  }
}
</style>

<style lang="scss">
.vertical-overlay-menu.menu-open .main-menu {
  display: flex !important;
  flex-direction: column !important;
  min-height: 100vh !important;
}
</style>
